import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { Select2 } from '@/components/forms/Select2';
import { Select2Async } from '@/components/forms/Select2Async';
import { Switch } from '@/components/forms/Switch';
import { TimePicker } from '@/components/forms/TimePicker';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { AsyncButton } from '@/components/common/AsyncButton';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { ApiError } from '@/api/client/abpError';
import { tsTemplatesApi } from '@/api/services/tsTemplates';
import { filterDataApi, filterDataPaged } from '@/api/services/filterData';
import {
  TsAlertProfileType,
  TsComparisonOperator,
  TsReason,
  TsTradeSession,
  type CreateUpdateTsAlertTemplateInput,
  type TsAlertTemplateListItemDto,
} from '@/api/types/tsTemplates';
import { ViewTemplateModal } from './ViewTemplateModal';
import { TsTemplatePermissions } from './permissions';
import { useCreateTemplate, useDeleteTemplate, usePriorities, useSetTemplateEnabled, useUpdateTemplate } from './api';
import './TsTemplatesPage.css';

// ---- static option lists ----
const TYPE_OPTS = [
  { value: String(TsAlertProfileType.AccountConditions), label: 'Account Conditions' },
  { value: String(TsAlertProfileType.ClosedTrades), label: 'Closed Trades' },
  { value: String(TsAlertProfileType.Deals), label: 'Deals' },
];
const OP_OPTS = [
  { value: String(TsComparisonOperator.NotApplicable), label: 'N/A' },
  { value: String(TsComparisonOperator.GreaterThan), label: '>' },
  { value: String(TsComparisonOperator.SmallerThan), label: '<' },
  { value: String(TsComparisonOperator.EqualTo), label: '=' },
];
const SESSION_OPTS = [
  { value: String(TsTradeSession.NotApplicable), label: 'N/A' },
  { value: String(TsTradeSession.Sydney), label: 'Sydney' },
  { value: String(TsTradeSession.Tokyo), label: 'Tokyo' },
  { value: String(TsTradeSession.London), label: 'London' },
  { value: String(TsTradeSession.NewYork), label: 'New York' },
  { value: String(TsTradeSession.Custom), label: 'Custom' },
];
const REASON_OPTS = [
  { value: String(TsReason.Client), label: 'Client' },
  { value: String(TsReason.Expert), label: 'Expert' },
  { value: String(TsReason.Dealer), label: 'Dealer' },
  { value: String(TsReason.Signal), label: 'Signal' },
  { value: String(TsReason.Mobile), label: 'Mobile' },
  { value: String(TsReason.Web), label: 'Web' },
];
// GMT session windows, shifted to local time for preset sessions (legacy ts-alert-profile-form).
const SESSION_WINDOWS: Record<number, [string, string]> = {
  1: ['21:00:00', '05:59:59'],
  2: ['00:00:00', '08:59:59'],
  3: ['07:00:00', '15:59:59'],
  4: ['12:00:00', '20:59:59'],
};
function shiftTime(hms: string, offsetMin: number): string {
  const [h, m, s] = hms.split(':').map(Number);
  let total = (((h * 60 + m + offsetMin) % 1440) + 1440) % 1440;
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface FormState {
  profileTag: string;
  priority: string;
  historyDate: string;
  type: string;
  reason: string;
  countryId: string;
  mifidClassification: string;
  marInstruments: boolean;
  tradeTime: string;
  tradePoints: string;
  volumeOperator: string;
  volume: string;
  notionalOperator: string;
  notional: string;
  tradeSession: string;
  openTimeRaw: string;
  closeTimeRaw: string;
  profitThresholdsPerTrade: boolean;
  profitThresholdsAmountPerTrade: string;
  cumulativeProfitThresholds: boolean;
  cumulativeProfitThresholdsAmount: string;
  cumulativeProfitThresholdsFloating: boolean;
  equityOperator: string;
  equity: string;
  marginLevelOperator: string;
  marginLevel: string;
  overnightDays: string;
}

function emptyForm(): FormState {
  return {
    profileTag: '', priority: 'Low', historyDate: todayIso(), type: String(TsAlertProfileType.Deals),
    reason: '', countryId: '', mifidClassification: '', marInstruments: false,
    tradeTime: '', tradePoints: '', volumeOperator: '0', volume: '', notionalOperator: '0', notional: '',
    tradeSession: '0', openTimeRaw: '', closeTimeRaw: '',
    profitThresholdsPerTrade: false, profitThresholdsAmountPerTrade: '',
    cumulativeProfitThresholds: false, cumulativeProfitThresholdsAmount: '', cumulativeProfitThresholdsFloating: false,
    equityOperator: '0', equity: '', marginLevelOperator: '0', marginLevel: '', overnightDays: '',
  };
}

type Dis = Partial<Record<string, boolean>>;

// Faithful port of the legacy ts-alert-profile-form enable/disable cascade
// (applyTypeState + applyTradeSession + syncProfitThresholdModes/applyCumulativeCompatibility +
// overnight interlock + checkIfTradesAreEmpty/checkIfUsersAreEmpty + syncVolumeNotionalMutualExclusion).
// It BOTH resets the values of disabled fields (so stale values don't trigger interlocks) AND
// returns the disabled map. Idempotent, so it can run on every change.
function normalize(input: FormState): { f: FormState; dis: Dis } {
  const f: FormState = { ...input };
  const dis: Dis = {};
  const t = Number(f.type);
  const today = todayIso();

  // --- applyTypeState: reset + disable the fields each type doesn't use ---
  if (t === TsAlertProfileType.Deals) {
    f.tradeTime = ''; f.tradePoints = '';
    f.equityOperator = '0'; f.equity = '';
    f.marginLevelOperator = '0'; f.marginLevel = '';
    f.overnightDays = '';
    f.profitThresholdsPerTrade = false; f.profitThresholdsAmountPerTrade = '';
    f.cumulativeProfitThresholds = false; f.cumulativeProfitThresholdsAmount = ''; f.cumulativeProfitThresholdsFloating = false;
    dis.tradeTime = dis.tradePoints = dis.equity = dis.marginLevel = dis.overnightDays = dis.perTrade = dis.cumulative = true;
  } else if (t === TsAlertProfileType.ClosedTrades) {
    f.equityOperator = '0'; f.equity = '';
    f.marginLevelOperator = '0'; f.marginLevel = '';
    f.overnightDays = '';
    dis.equity = dis.marginLevel = dis.overnightDays = true;
  } else {
    // Account Conditions: trade fields off; Equity/Margin/Overnight stay available.
    f.tradeTime = ''; f.tradePoints = '';
    f.volumeOperator = '0'; f.volume = '';
    f.notionalOperator = '0'; f.notional = '';
    f.tradeSession = '0'; f.openTimeRaw = ''; f.closeTimeRaw = '';
    f.profitThresholdsPerTrade = false; f.profitThresholdsAmountPerTrade = '';
    f.cumulativeProfitThresholds = false; f.cumulativeProfitThresholdsAmount = ''; f.cumulativeProfitThresholdsFloating = false;
    dis.tradeTime = dis.tradePoints = dis.session = dis.perTrade = dis.cumulative = true;
  }

  // --- applyTradeSession: preset windows (read-only) / Custom (editable) / N/A (hidden) ---
  const s = Number(f.tradeSession);
  if (s >= 1 && s <= 4) {
    const off = -new Date().getTimezoneOffset();
    const [o, c] = SESSION_WINDOWS[s];
    f.openTimeRaw = shiftTime(o, off);
    f.closeTimeRaw = shiftTime(c, off);
  } else if (s === 0) {
    f.openTimeRaw = ''; f.closeTimeRaw = '';
  }

  // --- cumulative compatibility (only reachable when profit checks enabled, i.e. Closed Trades) ---
  const cumulativeLock = f.cumulativeProfitThresholds && !dis.cumulative;
  if (cumulativeLock) {
    dis.perTrade = true; f.profitThresholdsPerTrade = false; f.profitThresholdsAmountPerTrade = '';
    f.tradeTime = ''; dis.tradeTime = true;
    f.tradePoints = ''; dis.tradePoints = true;
    f.reason = ''; dis.reason = true;
    f.openTimeRaw = ''; f.closeTimeRaw = '';
    f.overnightDays = ''; dis.overnightDays = true;
    f.equityOperator = '0'; f.equity = ''; dis.equity = true;
    f.marginLevelOperator = '0'; f.marginLevel = ''; dis.marginLevel = true;
  } else if (f.profitThresholdsPerTrade && !dis.perTrade) {
    dis.cumulative = true;
  }

  // --- overnight interlock (keyup handler): overnight set => lock out equity/margin/trade + date ---
  if (f.overnightDays !== '') {
    dis.equity = true; dis.marginLevel = true; dis.tradeTime = true; dis.tradePoints = true;
    f.equityOperator = '0'; f.equity = ''; f.marginLevelOperator = '0'; f.marginLevel = '';
    f.tradeTime = ''; f.tradePoints = '';
    dis.historyDate = true; f.historyDate = today;
  }

  // --- checkIfTradesAreEmpty / checkIfUsersAreEmpty (skipped for Closed Trades/Deals/cumulative) ---
  if (!(t === TsAlertProfileType.ClosedTrades || t === TsAlertProfileType.Deals || cumulativeLock)) {
    const anyTrade =
      Number(f.volumeOperator) !== 0 || Number(f.notionalOperator) !== 0 ||
      f.tradePoints !== '' || f.tradeTime !== '' ||
      f.profitThresholdsPerTrade || f.cumulativeProfitThresholds ||
      f.openTimeRaw !== '' || f.closeTimeRaw !== '';
    if (anyTrade || f.overnightDays !== '') { dis.equity = true; dis.marginLevel = true; }
    const noUser = Number(f.marginLevelOperator) === 0 && Number(f.equityOperator) === 0;
    dis.overnightDays = !noUser;
    if (!noUser) { dis.historyDate = true; f.historyDate = today; }
  }

  // --- syncVolumeNotionalMutualExclusion (owns volume/notional disabled state) ---
  const isAcct = t === TsAlertProfileType.AccountConditions;
  const overnightsSet = f.overnightDays !== '';
  const volumeOn = Number(f.volumeOperator) !== 0;
  const notionalOn = Number(f.notionalOperator) !== 0;
  if (cumulativeLock) {
    dis.volume = true; f.volumeOperator = '0'; f.volume = '';
  } else if (isAcct && !overnightsSet) {
    dis.volume = true; dis.notional = true;
  } else if (volumeOn && !notionalOn) {
    dis.notional = true; f.notionalOperator = '0'; f.notional = '';
  } else if (notionalOn && !volumeOn) {
    dis.volume = true; f.volumeOperator = '0'; f.volume = '';
  }

  return { f, dis };
}

export function TsTemplatesPage() {
  const can = usePermission();
  const canCreate = can(TsTemplatePermissions.create);
  const canEdit = can(TsTemplatePermissions.edit);
  const canDelete = can(TsTemplatePermissions.delete);
  const toast = useToast();

  const [form, setForm] = useState<FormState>(() => normalize(emptyForm()).f);
  const [editId, setEditId] = useState<number | null>(null);
  const [viewId, setViewId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TsAlertTemplateListItemDto | null>(null);
  const [toggleTarget, setToggleTarget] = useState<TsAlertTemplateListItemDto | null>(null);

  const priorities = usePriorities();
  const mifid = useQuery({ queryKey: ['rd-filter', 'mifid'], queryFn: filterDataApi.getMifidClassifications, staleTime: 5 * 60_000 });
  const active = useQuery({ queryKey: ['tsTemplates', 'active'], queryFn: () => tsTemplatesApi.getActive({ skipCount: 0, maxResultCount: 1000 }) });
  const deleted = useQuery({ queryKey: ['tsTemplates', 'deleted'], queryFn: () => tsTemplatesApi.getDeleted({ skipCount: 0, maxResultCount: 1000 }) });

  const createMut = useCreateTemplate();
  const updateMut = useUpdateTemplate();
  const setEnabledMut = useSetTemplateEnabled();
  const deleteMut = useDeleteTemplate();

  const dis = useMemo(() => normalize(form).dis, [form]);
  const apply = (partial: Partial<FormState>) => setForm((f) => normalize({ ...f, ...partial }).f);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => apply({ [k]: v } as Partial<FormState>);
  const onSession = (v: string | null) => apply({ tradeSession: v || '0' });
  // Per-trade / cumulative are mutually exclusive (normalize enforces the rest).
  const onPerTrade = (c: boolean) => apply(c ? { profitThresholdsPerTrade: true, cumulativeProfitThresholds: false } : { profitThresholdsPerTrade: false });
  const onCumulative = (c: boolean) => apply(c ? { cumulativeProfitThresholds: true, profitThresholdsPerTrade: false } : { cumulativeProfitThresholds: false });

  const numOrNull = (v: string) => (v === '' ? null : Number(v));
  const opOrNull = (v: string) => (Number(v) === 0 ? null : Number(v));

  function buildDto(): CreateUpdateTsAlertTemplateInput {
    const sessionTimes = Number(form.tradeSession) && !dis.session;
    let openTime: string | null = null;
    let closeTime: string | null = null;
    if (sessionTimes && form.openTimeRaw && form.closeTimeRaw) {
      openTime = `${form.historyDate}T${form.openTimeRaw}`;
      closeTime = `${form.historyDate}T${form.closeTimeRaw}`;
      if (closeTime <= openTime) {
        const d = new Date(`${form.historyDate}T00:00:00`);
        d.setDate(d.getDate() + 1);
        closeTime = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${form.closeTimeRaw}`;
      }
    }
    // `form` is already normalized (disabled fields reset to defaults), so send its values directly.
    return {
      profileTag: form.profileTag.trim(),
      priority: form.priority || null,
      historyDate: form.historyDate,
      type: Number(form.type),
      reason: form.reason === '' ? null : Number(form.reason),
      countryId: form.countryId === '' ? null : Number(form.countryId),
      mifidClassification: form.mifidClassification === '' ? null : Number(form.mifidClassification),
      marInstruments: form.marInstruments,
      tradeTime: numOrNull(form.tradeTime),
      tradePoints: numOrNull(form.tradePoints),
      volumeOperator: opOrNull(form.volumeOperator),
      volume: Number(form.volumeOperator) === 0 ? null : numOrNull(form.volume),
      notionalOperator: opOrNull(form.notionalOperator),
      notional: Number(form.notionalOperator) === 0 ? null : numOrNull(form.notional),
      tradeSession: opOrNull(form.tradeSession),
      openTime,
      closeTime,
      profitThresholdsPerTrade: form.profitThresholdsPerTrade,
      profitThresholdsAmountPerTrade: form.profitThresholdsPerTrade ? numOrNull(form.profitThresholdsAmountPerTrade) : null,
      cumulativeProfitThresholds: form.cumulativeProfitThresholds,
      cumulativeProfitThresholdsAmount: form.cumulativeProfitThresholds ? numOrNull(form.cumulativeProfitThresholdsAmount) : null,
      cumulativeProfitThresholdsFloating: form.cumulativeProfitThresholds && form.cumulativeProfitThresholdsFloating,
      equityOperator: opOrNull(form.equityOperator),
      equity: Number(form.equityOperator) === 0 ? null : numOrNull(form.equity),
      marginLevelOperator: opOrNull(form.marginLevelOperator),
      marginLevel: Number(form.marginLevelOperator) === 0 ? null : numOrNull(form.marginLevel),
      overnightDays: numOrNull(form.overnightDays),
      reportingCurrency: true,
    };
  }

  function validate(): string | null {
    if (!form.profileTag.trim()) return 'Template Name is required.';
    if (/^\d+$/.test(form.profileTag.trim())) return 'Template Name cannot be only numbers.';
    if (!dis.historyDate && !form.historyDate) return 'Historical Date is required.';
    if (Number(form.volumeOperator) !== 0 && !dis.volume && form.volume === '') return 'Volume amount is required.';
    if (Number(form.notionalOperator) !== 0 && !dis.notional && form.notional === '') return 'Notional amount is required.';
    return null;
  }

  const clearForm = () => { setForm(normalize(emptyForm()).f); setEditId(null); };

  async function submit() {
    const err = validate();
    if (err) { toast.error(err); return; }
    const dto = buildDto();
    try {
      if (editId != null) {
        await updateMut.mutateAsync({ id: editId, input: dto });
        toast.success('Template updated successfully!');
      } else {
        await createMut.mutateAsync(dto);
        toast.success('Template added successfully!');
      }
      clearForm();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to save the template.');
    }
  }

  async function loadForEdit(id: number) {
    try {
      const p = await tsTemplatesApi.get(id);
      setEditId(id);
      setForm(normalize({
        profileTag: p.profileTag ?? '',
        priority: p.priority ?? 'Low',
        historyDate: (p.historyDate ?? todayIso()).substring(0, 10),
        type: String(p.type ?? TsAlertProfileType.Deals),
        reason: p.reason == null ? '' : String(p.reason),
        countryId: p.countryId == null ? '' : String(p.countryId),
        mifidClassification: p.mifidClassification == null ? '' : String(p.mifidClassification),
        marInstruments: p.marInstruments,
        tradeTime: p.tradeTime == null ? '' : String(p.tradeTime),
        tradePoints: p.tradePoints == null ? '' : String(p.tradePoints),
        volumeOperator: String(p.volumeOperator ?? 0),
        volume: p.volume == null ? '' : String(p.volume),
        notionalOperator: String(p.notionalOperator ?? 0),
        notional: p.notional == null ? '' : String(p.notional),
        tradeSession: String(p.tradeSession ?? 0),
        openTimeRaw: p.openTime ? p.openTime.substring(11, 19) : '',
        closeTimeRaw: p.closeTime ? p.closeTime.substring(11, 19) : '',
        profitThresholdsPerTrade: p.profitThresholdsPerTrade,
        profitThresholdsAmountPerTrade: p.profitThresholdsAmountPerTrade == null ? '' : String(p.profitThresholdsAmountPerTrade),
        cumulativeProfitThresholds: p.cumulativeProfitThresholds,
        cumulativeProfitThresholdsAmount: p.cumulativeProfitThresholdsAmount == null ? '' : String(p.cumulativeProfitThresholdsAmount),
        cumulativeProfitThresholdsFloating: p.cumulativeProfitThresholdsFloating,
        equityOperator: String(p.equityOperator ?? 0),
        equity: p.equity == null ? '' : String(p.equity),
        marginLevelOperator: String(p.marginLevelOperator ?? 0),
        marginLevel: p.marginLevel == null ? '' : String(p.marginLevel),
        overnightDays: p.overnightDays == null ? '' : String(p.overnightDays),
      }).f);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      toast.error('Failed to load the template.');
    }
  }

  function confirmToggle() {
    if (!toggleTarget) return;
    const enabled = !toggleTarget.enabled;
    setEnabledMut.mutate({ id: toggleTarget.id, enabled }, {
      onSuccess: () => { toast.success(enabled ? 'Template enabled.' : 'Template disabled.'); setToggleTarget(null); },
      onError: (e) => { toast.error(e instanceof ApiError ? e.message : 'Failed to update the template.'); setToggleTarget(null); },
    });
  }
  function confirmDelete() {
    if (!deleteTarget) return;
    deleteMut.mutate(deleteTarget.id, {
      onSuccess: () => { toast.success('Template deleted.'); if (editId === deleteTarget.id) clearForm(); setDeleteTarget(null); },
      onError: (e) => { toast.error(e instanceof ApiError ? e.message : 'Failed to delete the template.'); setDeleteTarget(null); },
    });
  }

  const sessionTimesVisible = Number(form.tradeSession) !== 0 && !dis.session;

  return (
    <div className="ts-templates-page">
      <PageHeader title="TS Alert Profile Templates" breadcrumb={['Home', 'Trader Surveillance', 'TS Alert Profile Templates']} />

      {/* Buttons bar */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        {canCreate && (
          <AsyncButton className="btn btn-primary btn-sm" icon={editId != null ? 'fa fa-save' : 'fa fa-plus'} onClick={submit}>
            {editId != null ? 'Save' : 'Create'}
          </AsyncButton>
        )}
        <button type="button" className="btn btn-link btn-sm" onClick={clearForm}>{editId != null ? 'Cancel' : 'Clear All Filters'}</button>
        <div className="ms-auto">
          <Switch label="Reporting Currency" checked disabled onChange={() => {}} />
        </div>
      </div>

      <div className="row g-2">
        {/* Left: authoring form */}
        <div className="col-lg-9">
          <Section title="Template Info">
              <div className="row g-2">
                <div className="col-lg-4">
                  <label className="form-label">Template Name</label>
                  <input className="form-control" value={form.profileTag} onChange={(e) => set('profileTag', e.target.value)} />
                </div>
                <div className="col-lg-4">
                  <label className="form-label">Historical Date</label>
                  <DateRangePicker mode="single" value={form.historyDate} onChange={(v) => set('historyDate', v)} disabled={dis.historyDate} />
                </div>
                <Select2 key="priority" className="col-lg-4" label="Priority" searchable={false} allowClear={false} value={form.priority} options={(priorities.data ?? []).map((p) => ({ value: p.value, label: p.text }))} onChange={(v) => set('priority', v || 'Low')} />
              </div>
          </Section>

          <Section title="Filters">
              <div className="row g-2 align-items-end">
                <Select2 key="reason" className="col-lg-4" label="Reason" searchable={false} allowClear placeholder="All" value={form.reason} options={REASON_OPTS} onChange={(v) => set('reason', v ?? '')} disabled={dis.reason} />
                <div className="col-lg-4">
                  <label className="form-label">Country</label>
                  <Select2Async placeholder="All" value={form.countryId || null} fetch={filterDataPaged.countries} onChange={(v) => set('countryId', v ?? '')} />
                </div>
                <Select2 key="mifid" className="col-lg-4" label="MiFID Classification" searchable={false} allowClear placeholder="All" value={form.mifidClassification} options={mifid.data ?? []} onChange={(v) => set('mifidClassification', v ?? '')} />
                <div className="col-lg-4">
                  <Switch label="MAR Instruments" checked={form.marInstruments} onChange={(c) => set('marInstruments', c)} />
                </div>
              </div>
          </Section>

          <Section title="Conditions">
              <div className="row g-2 align-items-end mb-2">
                <Select2 key="type" className="col-lg-4" label="Type" searchable={false} allowClear={false} value={form.type} options={TYPE_OPTS} onChange={(v) => set('type', v || String(TsAlertProfileType.Deals))} />
              </div>

              <h6 className="fw-semibold border-top pt-2 mt-2">Trade Conditions</h6>
              <div className="row g-2 align-items-end">
                <NumField className="col-lg-3" label="Trade Time" suffix="seconds" value={form.tradeTime} disabled={dis.tradeTime} onChange={(v) => set('tradeTime', v)} />
                <NumField className="col-lg-3" label="Trade Points" suffix="points" value={form.tradePoints} disabled={dis.tradePoints} onChange={(v) => set('tradePoints', v)} />
                <OpAmount className="col-lg-6" label="Volume" op={form.volumeOperator} amount={form.volume} disabled={dis.volume} onOp={(v) => set('volumeOperator', v)} onAmount={(v) => set('volume', v)} />
                <OpAmount className="col-lg-6" label="Notional" op={form.notionalOperator} amount={form.notional} disabled={dis.notional} onOp={(v) => set('notionalOperator', v)} onAmount={(v) => set('notional', v)} />
                <Select2 key="session" className="col-lg-3" label="Trading Session" searchable={false} allowClear={false} value={form.tradeSession} options={SESSION_OPTS} onChange={onSession} disabled={dis.session} />
                {sessionTimesVisible && (
                  <>
                    <div className="col-lg-3"><label className="form-label">Open Time</label><TimePicker value={form.openTimeRaw} onChange={(v) => set('openTimeRaw', v)} /></div>
                    <div className="col-lg-3"><label className="form-label">Close Time</label><TimePicker value={form.closeTimeRaw} onChange={(v) => set('closeTimeRaw', v)} /></div>
                  </>
                )}
              </div>

              <h6 className="fw-semibold border-top pt-2 mt-3">Profit Thresholds</h6>
              <div className="row g-2 align-items-center">
                <div className="col-lg-3"><Switch label="P/L Threshold (per trade)" checked={form.profitThresholdsPerTrade} disabled={dis.perTrade} onChange={onPerTrade} /></div>
                {form.profitThresholdsPerTrade && !dis.perTrade && (
                  <NumField className="col-lg-3" label="Amount" allowNegative value={form.profitThresholdsAmountPerTrade} onChange={(v) => set('profitThresholdsAmountPerTrade', v)} />
                )}
                <div className="col-lg-3"><Switch label="Cumulative P/L Threshold" checked={form.cumulativeProfitThresholds} disabled={dis.cumulative} onChange={onCumulative} /></div>
                {form.cumulativeProfitThresholds && !dis.cumulative && (
                  <>
                    <NumField className="col-lg-3" label="Amount" allowNegative value={form.cumulativeProfitThresholdsAmount} onChange={(v) => set('cumulativeProfitThresholdsAmount', v)} />
                    <div className="col-lg-3"><Switch label="Include Unrealized P/L" checked={form.cumulativeProfitThresholdsFloating} onChange={(c) => set('cumulativeProfitThresholdsFloating', c)} /></div>
                  </>
                )}
              </div>

              <h6 className="fw-semibold border-top pt-2 mt-3">Account Conditions</h6>
              <div className="row g-2 align-items-end">
                <OpAmount className="col-lg-6" label="Equity" op={form.equityOperator} amount={form.equity} disabled={dis.equity} onOp={(v) => set('equityOperator', v)} onAmount={(v) => set('equity', v)} />
                <OpAmount className="col-lg-6" label="Margin Level %" op={form.marginLevelOperator} amount={form.marginLevel} disabled={dis.marginLevel} onOp={(v) => set('marginLevelOperator', v)} onAmount={(v) => set('marginLevel', v)} />
              </div>

              <h6 className="fw-semibold border-top pt-2 mt-3">Overnight (Open Positions)</h6>
              <div className="row g-2 align-items-end">
                <NumField className="col-lg-3" label="Overnight Days" suffix="days" value={form.overnightDays} disabled={dis.overnightDays} onChange={(v) => set('overnightDays', v)} />
              </div>
          </Section>
        </div>

        {/* Right: template lists */}
        <div className="col-lg-3">
          <TemplateList
            title="Active Templates"
            items={active.data?.items ?? []}
            loading={active.isLoading}
            variant="active"
            canEdit={canEdit}
            canDelete={canDelete}
            onEdit={loadForEdit}
            onToggle={setToggleTarget}
            onDelete={setDeleteTarget}
            onView={setViewId}
          />
          <TemplateList
            title="Deleted Templates"
            items={deleted.data?.items ?? []}
            loading={deleted.isLoading}
            variant="deleted"
            canEdit={false}
            canDelete={false}
            onView={setViewId}
          />
        </div>
      </div>

      <ViewTemplateModal id={viewId} onClose={() => setViewId(null)} />

      <ConfirmDialog
        show={toggleTarget !== null}
        title="Warning!"
        message={toggleTarget ? `${toggleTarget.enabled ? 'Disable' : 'Enable'} this template?` : ''}
        confirmLabel={toggleTarget?.enabled ? 'Disable' : 'Enable'}
        confirmVariant="primary"
        busy={setEnabledMut.isPending}
        onConfirm={confirmToggle}
        onCancel={() => setToggleTarget(null)}
      />
      <ConfirmDialog
        show={deleteTarget !== null}
        title="Warning!"
        message={`Are you sure you want to delete "${deleteTarget?.profileTag ?? ''}" Template ?`}
        confirmLabel="Delete"
        busy={deleteMut.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

// Collapsible section — header turns primary-tinted when open (legacy accordion look).
function Section({ title, defaultOpen = true, children }: { title: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card custom-card mb-2">
      <div
        className={`card-header d-flex align-items-center justify-content-between${open ? ' fk-accordion-open' : ''}`}
        role="button"
        onClick={() => setOpen((o) => !o)}
      >
        <div className="card-title mb-0">{title}</div>
        <i className={`fe ${open ? 'fe-chevron-up' : 'fe-chevron-down'}`} />
      </div>
      {open && <div className="card-body">{children}</div>}
    </div>
  );
}

function NumField({ className, label, suffix, value, onChange, disabled, allowNegative }: { className?: string; label: string; suffix?: string; value: string; onChange: (v: string) => void; disabled?: boolean; allowNegative?: boolean }) {
  return (
    <div className={className}>
      <label className="form-label">{label}</label>
      {suffix ? (
        <div className="input-group">
          <input type="number" className="form-control" value={value} disabled={disabled} min={allowNegative ? undefined : 0} onChange={(e) => onChange(e.target.value)} />
          <span className="input-group-text">{suffix}</span>
        </div>
      ) : (
        <input type="number" className="form-control" value={value} disabled={disabled} min={allowNegative ? undefined : 0} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

// Operator + amount pair (legacy col-lg-6 d-flex): the operator fills the half while the
// amount is hidden, then the two split 50/50 once an operator is chosen.
function OpAmount({ className, label, op, amount, disabled, onOp, onAmount }: { className?: string; label: string; op: string; amount: string; disabled?: boolean; onOp: (v: string) => void; onAmount: (v: string) => void }) {
  const showAmount = Number(op) !== 0 && !disabled;
  return (
    <div className={`${className ?? ''} d-flex align-items-end gap-1`}>
      <div style={{ flex: '1 1 0' }}>
        <label className="form-label">{label}</label>
        <Select2 searchable={false} allowClear={false} value={op} options={OP_OPTS} onChange={(v) => onOp(v || '0')} disabled={disabled} />
      </div>
      {showAmount && (
        <div style={{ flex: '1 1 0' }}>
          <input type="number" className="form-control" placeholder="Amount" value={amount} onChange={(e) => onAmount(e.target.value)} />
        </div>
      )}
    </div>
  );
}

function TemplateList({ title, items, loading, variant, canEdit, canDelete, onEdit, onToggle, onDelete, onView }: {
  title: string;
  items: TsAlertTemplateListItemDto[];
  loading: boolean;
  variant: 'active' | 'deleted';
  canEdit: boolean;
  canDelete: boolean;
  onEdit?: (id: number) => void;
  onToggle?: (row: TsAlertTemplateListItemDto) => void;
  onDelete?: (row: TsAlertTemplateListItemDto) => void;
  onView: (id: number) => void;
}) {
  const [open, setOpen] = useState(variant === 'active');
  return (
    <div className="card custom-card mb-2">
      <div
        className={`card-header d-flex align-items-center justify-content-between${open ? ' fk-accordion-open' : ''}`}
        role="button"
        onClick={() => setOpen((o) => !o)}
      >
        <div className="card-title mb-0">{title} ({items.length})</div>
        <i className={`fe ${open ? 'fe-chevron-up' : 'fe-chevron-down'}`} />
      </div>
      {open && (
        <div className="card-body p-2">
          {loading ? (
            <div className="text-center text-muted py-2"><i className="fa fa-spinner fa-spin me-1" />Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center text-muted py-2 fs-13">No templates.</div>
          ) : (
            items.map((t) => (
              <div key={t.id} className="d-flex align-items-center justify-content-between py-1 border-bottom">
                <span className="d-inline-flex align-items-center text-truncate">
                  {variant === 'active' && <span className={`rounded-circle d-inline-block me-2 ${t.enabled ? 'bg-primary' : 'bg-secondary'}`} style={{ width: 8, height: 8 }} />}
                  <span className="fs-13 fw-semibold text-truncate">{t.profileTag}</span>
                </span>
                <span className="d-inline-flex align-items-center gap-1">
                  {variant === 'deleted' ? (
                    <button type="button" className="btn btn-sm btn-outline-info" title="View" onClick={() => onView(t.id)}><i className="fa fa-eye" /></button>
                  ) : (
                    <>
                      {canEdit && <button type="button" className="btn btn-sm btn-outline-primary" title="Edit" onClick={() => onEdit?.(t.id)}><i className="fa fa-edit" /></button>}
                      {canDelete && <button type="button" className="btn btn-sm btn-outline-danger" title="Delete" onClick={() => onDelete?.(t)}><i className="fa fa-trash" /></button>}
                      {canEdit && <Switch checked={t.enabled} onChange={() => onToggle?.(t)} />}
                    </>
                  )}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
